import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { AlignmentTypeahead } from './AlignmentTypeahead';
import { useAuth } from '@/state/auth/AuthProvider';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { useAlignmentRoadmapScope } from './AlignmentRoadmapScopeContext';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  TagIcon, 
  LinkIcon,
  LinkSlashIcon
} from '@heroicons/react/24/outline';

interface Strategy {
  id: string;
  name: string;
  description?: string;
  strategy_type: 'GROWTH' | 'MARGIN_EXPANSION' | 'REGULATORY' | 'OTHER' | 'ENABLER';
  owner_user_id: string;
  owner_name: string;
  tenant_id: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  objective_count: number;
  project_count: number;
  linked_objectives?: Array<{ id: string; name: string; link_id: string }>;
  project_names?: string;
  created_at: string;
  updated_at: string;
}

interface StrategyFormData {
  name: string;
  description: string;
  strategy_type: 'GROWTH' | 'MARGIN_EXPANSION' | 'REGULATORY' | 'OTHER' | 'ENABLER';
}

interface ProjectLink {
  transformation_id: string;
  transformation_name: string;
  strategy_id: string;
  contribution_level: string;
  notes?: string;
}


export const StrategyManagement: React.FC = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { listParams, scopeKey } = useAlignmentRoadmapScope();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [availableProjects, setAvailableProjects] = useState<any[]>([]);

  const [strategyData, setStrategyData] = useState<Map<string, { projects: ProjectLink[] }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLinkProjectDialogOpen, setIsLinkProjectDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<Strategy | null>(null);
  const [selectedStrategyForProject, setSelectedStrategyForProject] = useState<Strategy | null>(null);
  const [formData, setFormData] = useState<StrategyFormData>({
    name: '',
    description: '',
    strategy_type: 'GROWTH'
  });
  const [projectLinkFormData, setProjectLinkFormData] = useState({
    transformation_id: '',
    contribution_level: 'HIGH' as 'HIGH' | 'MEDIUM' | 'LOW',
    notes: ''
  });

  useEffect(() => {
    if (user?.tenantId) {
      fetchStrategies();
      fetchAvailableProjects();
    }
  }, [user?.tenantId, scopeKey]);

  // Fetch data for strategies when they change
  useEffect(() => {
    if (strategies.length > 0) {
      strategies.forEach(strategy => {
        if (strategy.project_count > 0) {
          fetchProjectLinks(strategy.id);
        }
      });
    }
  }, [strategies]);



  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/alignment/strategies/${user?.tenantId}`, listParams);

      if (response.data.success) {
        setStrategies(response.data.data);
      } else {
        throw new Error(response.data.message || t('alignment.strategy.errors.failedToFetchStrategies'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    } finally {
      setLoading(false);
    }
  };



  const fetchAvailableProjects = async () => {
    try {
      const response = await api.get('/transformations');
      if (response.data.success) {
        setAvailableProjects(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };




  const fetchProjectLinks = async (strategyId: string) => {
    try {
      // Only fetch explicit project links for Strategy Management page
      const response = await api.get(`/alignment/project-strategy-links/strategy/${strategyId}`);
      
      if (response.data.success) {
        setStrategyData(prev => new Map(prev.set(strategyId, {
          ...prev.get(strategyId) || { projects: [] },
          projects: response.data.data
        })));
      }
    } catch (err) {
      console.error('Failed to fetch project links:', err);
    }
  };

  const handleCreateStrategy = async () => {
    try {
      const response = await api.post('/alignment/strategies', {
        ...formData,
        owner_user_id: user?.id,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsCreateDialogOpen(false);
        setFormData({
          name: '',
          description: '',
          strategy_type: 'GROWTH'
        });
        fetchStrategies();
      } else {
        throw new Error(response.data.message || t('alignment.strategy.errors.failedToCreateStrategy'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    }
  };

  const handleUpdateStrategy = async () => {
    if (!editingStrategy) return;

    try {
      const response = await api.put(`/alignment/strategies/${editingStrategy.id}`, {
        name: formData.name,
        description: formData.description,
        strategy_type: formData.strategy_type,
      });

      if (response.data.success) {
        setIsEditDialogOpen(false);
        setEditingStrategy(null);
        fetchStrategies();
      } else {
        throw new Error(response.data.message || t('alignment.strategy.errors.failedToUpdateStrategy'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    }
  };

  const handleDeleteStrategy = async (strategyId: string) => {
    if (!confirm(t('alignment.strategy.deleteConfirm'))) return;

    try {
      const response = await api.delete(`/alignment/strategies/${strategyId}`);

      if (response.data.success) {
        fetchStrategies();
      } else {
        throw new Error(response.data.message || t('alignment.strategy.errors.failedToDeleteStrategy'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    }
  };

  const handleLinkProject = async () => {
    if (!selectedStrategyForProject) return;

    try {
      const response = await api.post('/alignment/project-strategy-links', {
        transformation_id: projectLinkFormData.transformation_id,
        strategy_id: selectedStrategyForProject.id,
        contribution_level: projectLinkFormData.contribution_level,
        notes: projectLinkFormData.notes,
        tenant_id: user?.tenantId,
      });

      if (response.data.success) {
        setIsLinkProjectDialogOpen(false);
        setSelectedStrategyForProject(null);
        setProjectLinkFormData({
          transformation_id: '',
          contribution_level: 'HIGH',
          notes: ''
        });
        fetchStrategies(); // Refresh to show updated project count
        // Refresh the specific strategy's data
        if (selectedStrategyForProject) {
          fetchProjectLinks(selectedStrategyForProject.id);
        }
      } else {
        throw new Error(response.data.message || t('alignment.strategy.errors.failedToLinkProjectToStrategy'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    }
  };

  const openEditDialog = (strategy: Strategy) => {
    setEditingStrategy(strategy);
    setFormData({
      name: strategy.name,
      description: strategy.description || '',
      strategy_type: strategy.strategy_type
    });
    setIsEditDialogOpen(true);
  };

  const openLinkProjectDialog = (strategy: Strategy) => {
    setSelectedStrategyForProject(strategy);
    setProjectLinkFormData({
      transformation_id: '',
      contribution_level: 'HIGH',
      notes: ''
    });
    setIsLinkProjectDialogOpen(true);
    fetchProjectLinks(strategy.id);
  };



  const handleUnlinkProject = async (transformationId: string, strategyId: string) => {
    if (!confirm(t('alignment.strategy.unlinkProjectConfirm'))) return;

    try {
      await api.delete(`/alignment/project-strategy-links/${transformationId}/${strategyId}/${user?.tenantId}`);
      
      fetchStrategies(); // Refresh to show updated project count
      fetchProjectLinks(strategyId);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('alignment.strategy.errors.genericError'));
    }
  };

  const closeDialogs = () => {
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
    setIsLinkProjectDialogOpen(false);
    setEditingStrategy(null);
    setSelectedStrategyForProject(null);
    setFormData({
      name: '',
      description: '',
      strategy_type: 'GROWTH'
    });
    setProjectLinkFormData({
      transformation_id: '',
      contribution_level: 'HIGH',
      notes: ''
    });
  };

  const strategyTypeOptions = [
    { value: 'GROWTH', label: t('alignment.strategy.types.growth') },
    { value: 'MARGIN_EXPANSION', label: t('alignment.strategy.types.marginExpansion') },
    { value: 'REGULATORY', label: t('alignment.strategy.types.regulatory') },
    { value: 'OTHER', label: t('alignment.strategy.types.other') },
    { value: 'ENABLER', label: t('alignment.strategy.types.enabler') }
  ];

  const contributionLevelOptions = [
    { value: 'HIGH', label: t('alignment.strategy.contributionLevels.high') },
    { value: 'MEDIUM', label: t('alignment.strategy.contributionLevels.medium') },
    { value: 'LOW', label: t('alignment.strategy.contributionLevels.low') }
  ];

  if (loading) {
    return (
      <Card padding="none">
        <CardContent className="p-3">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-end">
        <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
          <PlusIcon className="w-4 h-4 mr-2" />
          {t('alignment.strategy.createNew')}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <span className="text-sm">⚠️</span>
            <span className="text-sm font-medium">{t('alignment.common.error')}</span>
          </div>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      {/* Strategies List */}
      <div className="grid gap-2">
        {strategies.map((strategy) => (
          <Card key={strategy.id} padding="none" className="hover:shadow-md transition-shadow">
            <CardContent className="p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="mb-1">
                    <h3
                      className="text-sm font-semibold text-[var(--color-surface-text)]"
                      title={`${t('alignment.strategy.owner')}: ${strategy.owner_name} · ${t('alignment.strategy.created')}: ${new Date(strategy.created_at).toLocaleDateString()}`}
                    >
                      {strategy.name}
                    </h3>
                  </div>
                  
                  {strategy.description && (
                    <p className="text-sm text-[var(--color-surface-text-secondary)] mb-1">
                      {strategy.description}
                    </p>
                  )}

                  <div className="mt-1">
                    <h4 className="text-xs font-medium text-[var(--color-surface-text)] mb-0.5">{t('alignment.strategy.sectionHeaders.explicitlyLinkedProjects')}</h4>
                    {strategyData.get(strategy.id)?.projects && strategyData.get(strategy.id)!.projects.length > 0 ? (
                      <ul className="list-disc pl-5 text-sm text-[var(--color-surface-text)]">
                        {strategyData.get(strategy.id)!.projects.map((link) => (
                          <li key={`${link.transformation_id}-${strategy.id}`} className="leading-5">
                            <span className="inline-flex items-center gap-1">
                              {link.transformation_name}
                              <button
                                type="button"
                                onClick={() => handleUnlinkProject(link.transformation_id, strategy.id)}
                                className="p-0.5 text-red-600 hover:text-red-700 flex-shrink-0"
                                title={t('alignment.strategy.buttonLabels.unlink') || 'Unlink'}
                                aria-label={t('alignment.strategy.buttonLabels.unlink') || 'Unlink'}
                              >
                                <LinkSlashIcon className="w-3.5 h-3.5" />
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-xs text-gray-500 italic">{t('alignment.strategy.noProjectsLinked')}</div>
                    )}
                  </div>
                </div>

                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openLinkProjectDialog(strategy)}
                    className="text-green-600 hover:text-green-700"
                  >
                    <LinkIcon className="w-4 h-4" />
                    {t('alignment.strategy.buttonLabels.linkProject')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(strategy)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteStrategy(strategy.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {strategies.length === 0 && (
          <Card padding="none">
            <CardContent className="p-4 text-center">
              <div className="text-[var(--color-surface-text-secondary)]">
                <TagIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">{t('alignment.noStrategiesCreated')}</h3>
                <p className="mb-4">{t('alignment.noStrategiesDescription')}</p>
                <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                  <PlusIcon className="w-4 h-4 mr-2" />
                  {t('alignment.createYourFirstStrategy')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Strategy Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.strategy.dialogTitles.createNew')}</h3>
            <div className="space-y-4">
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.strategyName')}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('alignment.strategy.enterStrategyName')}
                />
              </div>
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.strategy.describeStrategy')}
                  rows={3}
                />
              </div>
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.strategyType')}</label>
                <Select
                  value={formData.strategy_type}
                  onChange={(e) => setFormData({ ...formData, strategy_type: e.target.value as any })}
                  options={strategyTypeOptions}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.strategy.buttonLabels.cancel')}
              </Button>
              <Button onClick={handleCreateStrategy} className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                {t('alignment.strategy.buttonLabels.createStrategy')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Strategy Dialog */}
      {isEditDialogOpen && editingStrategy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.strategy.dialogTitles.edit')}</h3>
            <div className="space-y-4">
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.strategyName')}</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('alignment.strategy.enterStrategyName')}
                />
              </div>
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.description')}</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('alignment.strategy.describeStrategy')}
                  rows={3}
                />
              </div>
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.strategyType')}</label>
                <Select
                  value={formData.strategy_type}
                  onChange={(e) => setFormData({ ...formData, strategy_type: e.target.value as any })}
                  options={strategyTypeOptions}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.strategy.buttonLabels.cancel')}
              </Button>
              <Button onClick={handleUpdateStrategy} className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]">
                {t('alignment.strategy.buttonLabels.updateStrategy')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Link Project Dialog */}
      {isLinkProjectDialogOpen && selectedStrategyForProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{t('alignment.strategy.dialogTitles.linkProjectToStrategy')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('alignment.strategy.strategy')}: <strong>{selectedStrategyForProject.name}</strong>
            </p>
            <div className="space-y-4">
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.selectProject')}</label>
                <AlignmentTypeahead
                  value={projectLinkFormData.transformation_id}
                  onChange={(id) => setProjectLinkFormData({ ...projectLinkFormData, transformation_id: id })}
                  options={availableProjects.map(p => ({ id: p.id, label: p.name }))}
                  placeholder={t('alignment.strategy.placeholders.chooseProject')}
                />
              </div>

              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.contributionLevel')}</label>
                <Select
                  value={projectLinkFormData.contribution_level}
                  onChange={(e) => setProjectLinkFormData({ ...projectLinkFormData, contribution_level: e.target.value as any })}
                  options={contributionLevelOptions}
                />
              </div>
              <div>
<label className="block text-sm font-medium mb-1">{t('alignment.strategy.notesOptional')}</label>
                <Textarea
                  value={projectLinkFormData.notes}
                  onChange={(e) => setProjectLinkFormData({ ...projectLinkFormData, notes: e.target.value })}
                  placeholder={t('alignment.strategy.placeholders.addNotesAboutProjectLink')}
                  rows={2}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={closeDialogs} variant="outline" className="flex-1">
                {t('alignment.strategy.buttonLabels.cancel')}
              </Button>
              <Button 
                onClick={handleLinkProject} 
                disabled={!projectLinkFormData.transformation_id}
                className="flex-1 bg-[var(--color-primary-bg)] text-[var(--color-primary-text)] hover:bg-[var(--color-secondary-bg)]"
              >
                {t('alignment.strategy.buttonLabels.linkProject')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
